import { Link } from "react-router-dom";
import { Truck, Phone, Mail, MapPin } from "lucide-react";
import SocialIcons from "@/components/SocialIcons";
import { openConsentSettings } from "@/lib/consent";

const SERVICE_AREAS = [
  { slug: "lucan", label: "Lucan" },
  { slug: "tallaght", label: "Tallaght" },
  { slug: "blanchardstown", label: "Blanchardstown" },
  { slug: "clondalkin", label: "Clondalkin" },
  { slug: "swords", label: "Swords" },
  { slug: "dundrum", label: "Dundrum" },
  { slug: "sandyford", label: "Sandyford" },
  { slug: "rathmines", label: "Rathmines" },
  { slug: "castleknock", label: "Castleknock" },
  { slug: "blackrock", label: "Blackrock" },
  { slug: "dublin-6", label: "Dublin 6" },
  { slug: "dublin-8", label: "Dublin 8" },
  { slug: "dublin-12", label: "Dublin 12" },
];

const QUICK_LINKS = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Pricing", path: "/pricing" },
  { label: "Blog", path: "/blog" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
  { label: "Landlords & Estate Agents", path: "/landlords-estate-agents" },
  { label: "Moving & Cleaning Packages", path: "/moving-cleaning-packages" },
];

const SERVICES = [
  "Small Jobs / Single Item",
  "2 Room Move",
  "Full House Move",
  "Rubbish Removal",
  "Emergency Same-Day",
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0B1E36] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 bg-[#0055FF] rounded-sm flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-extrabold text-lg tracking-tight">
                Go<span className="text-[#0055FF]">Vans</span><span className="text-white/70 text-sm font-medium">.ie</span>
              </span>
            </div>
            <p className="text-white/80 text-sm leading-relaxed">
              Dublin&apos;s fast man with a van service. Same-day moves, competitive fixed prices, zero stress.
            </p>
            <div className="mt-5">
              <SocialIcons variant="dark" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-[0.2em] text-white/70 mb-5">Quick Links</h4>
            <div className="flex flex-col gap-3">
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-white/80 hover:text-white text-sm transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-[0.2em] text-white/70 mb-5">Services</h4>
            <div className="flex flex-col gap-3">
              {SERVICES.map((s) => (
                <Link key={s} to="/services" className="text-white/80 hover:text-white text-sm transition-colors">
                  {s}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-[0.2em] text-white/70 mb-5">Contact</h4>
            <div className="flex flex-col gap-4">
              <a href="tel:+353874199541" aria-label="Call GoVans.ie on 087 419 9541" className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors">
                <Phone className="w-4 h-4 shrink-0" aria-hidden="true" /> +353 87 419 9541
              </a>
              <a href="mailto:info@govans.ie" aria-label="Email GoVans.ie at info@govans.ie" className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors">
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" /> info@govans.ie
              </a>
              <div className="flex items-start gap-3 text-white/80 text-sm">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" /> Dublin, Ireland
              </div>
            </div>
          </div>
        </div>

        {/* Service areas — internal SEO links */}
        <div className="border-t border-white/10 mt-16 pt-10">
          <h4 className="font-bold text-sm uppercase tracking-[0.2em] text-white/70 mb-5">Service Areas Across Dublin</h4>
          <div className="flex flex-wrap gap-2">
            {SERVICE_AREAS.map((area) => (
              <Link
                key={area.slug}
                to={`/man-with-a-van-${area.slug}`}
                data-testid={`footer-area-${area.slug}`}
                className="text-xs sm:text-sm text-white/80 hover:text-white border border-white/25 hover:border-white/60 px-3 py-1.5 rounded-sm transition-colors"
              >
                {area.label}
              </Link>
            ))}
          </div>
        </div>

        {/* GoCleaning cross-promo footer link — related but separate business */}
        <div className="border-t border-white/10 mt-8 pt-6 text-center">
          <p className="text-white/60 text-xs sm:text-sm">
            Need professional cleaning?{" "}
            <a
              href="https://gocleaning.ie"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-gocleaning-link"
              className="text-white hover:text-white font-bold underline underline-offset-2"
            >
              Visit GoCleaning.ie
            </a>
          </p>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/70 text-sm">
            &copy; {new Date().getFullYear()} GoVans.ie &mdash; Dublin, Ireland. All rights reserved.
          </p>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-4 gap-y-2 text-white/70 text-sm">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span aria-hidden="true" className="text-white/30">·</span>
            <Link to="/cookies" data-testid="footer-cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
            <span aria-hidden="true" className="text-white/30">·</span>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
            <span aria-hidden="true" className="text-white/30">·</span>
            <button
              type="button"
              data-testid="footer-manage-cookies"
              onClick={openConsentSettings}
              className="hover:text-white transition-colors underline underline-offset-2"
            >
              Manage cookies
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
}
